import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCurrentUser, clearUser } from '../features/auth/authSlice';
import { needsEmailVerification } from '../utils/authVerification';

export default function ProtectedRoute({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, token, verificationRequired, mustChangePassword, status } = useSelector((state) => state.auth);
  const [isValidating, setIsValidating] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);

  useEffect(() => {
    const validateSession = async () => {
      const storedToken = localStorage.getItem('auth_token');

      if (!storedToken && !token) {
        setIsValidating(false);
        setSessionValid(false);
        return;
      }

      const storedNeedsVerification =
        localStorage.getItem('auth_verification_required') === 'true' ||
        (user && needsEmailVerification(user)) ||
        verificationRequired;

      if (storedNeedsVerification && (user || storedToken)) {
        setSessionValid(true);
        setIsValidating(false);
        return;
      }

      if (storedToken || token) {
        try {
          setIsValidating(true);

          if (!user || status === 'idle') {
            await dispatch(getCurrentUser()).unwrap();
            setSessionValid(true);
          } else if (user && status === 'succeeded') {
            setSessionValid(true);
          } else if (status === 'failed') {
            setSessionValid(false);
            dispatch(clearUser());
          } else if (status === 'loading') {
            return;
          }
        } catch (error) {
          if (error?.type === 'verification_required') {
            setSessionValid(true);
          } else {
            setSessionValid(false);
            dispatch(clearUser());
          }
        } finally {
          setIsValidating(false);
        }
      } else {
        setIsValidating(false);
        setSessionValid(false);
      }
    };

    validateSession();
  }, [dispatch, token, user, status, verificationRequired]);

  if (isValidating || (token && status === 'loading')) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Validating session...</span>
        </div>
      </div>
    );
  }

  const storedToken = localStorage.getItem('auth_token');
  if (!sessionValid || (!user && !storedToken) || (!token && !storedToken)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const mustSetupPassword =
    mustChangePassword || localStorage.getItem('auth_must_change_password') === 'true';

  if (mustSetupPassword && location.pathname !== '/auth/complete-invited-password-setup') {
    return <Navigate to="/auth/complete-invited-password-setup" replace />;
  }

  const mustVerify =
    verificationRequired ||
    localStorage.getItem('auth_verification_required') === 'true' ||
    needsEmailVerification(user);

  if (mustVerify) {
    return <Navigate to="/verify-email" replace />;
  }

  return children;
}
